// src/components/dashboard/BookBarcodeScannerModal.jsx
import React, { useEffect, useRef, useState, useCallback, memo } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Camera, X, RefreshCw, Search, AlertCircle, Upload, Zap, Sparkles, Key, Check, ChevronDown, ChevronUp } from "lucide-react";
import toast from "react-hot-toast";
import BookMetadataService from "../../services/dashboard/BookMetadataService";

// Memoized scanner container so React reconciliation never touches or removes children inside this div
const ScannerVideoContainer = memo(function ScannerVideoContainer({ containerId }) {
  return (
    <div
      id={containerId}
      className="w-full h-full [&_video]:w-full [&_video]:h-full [&_video]:object-cover"
    />
  );
});

export default function BookBarcodeScannerModal({
  isOpen,
  onClose,
  onBookFound,
  isFetching,
}) {
  const [activeTab, setActiveTab] = useState("camera"); // "camera" | "upload" | "manual"
  const [manualIsbn, setManualIsbn] = useState("");
  const [cameraError, setCameraError] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [frameCount, setFrameCount] = useState(0);

  // Google Books API Key setting state
  const [apiKeyInput, setApiKeyInput] = useState(BookMetadataService.getApiKey() || "");
  const [showApiKeySetting, setShowApiKeySetting] = useState(false);
  const [isKeySaved, setIsKeySaved] = useState(false);

  const scannerRef = useRef(null);
  const isStartingRef = useRef(false);
  const isStoppingRef = useRef(false);
  const nativeDetectorTimerRef = useRef(null);
  const fileInputRef = useRef(null);
  const hasFoundRef = useRef(false);
  const containerId = "html5-barcode-scanner-view";

  // Prevent browser's benign "play() request was interrupted" error overlay in development
  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      const msg =
        event?.reason?.message ||
        (typeof event?.reason === "string" ? event.reason : "") ||
        "";
      const name = event?.reason?.name || "";
      if (
        name === "AbortError" ||
        msg.toLowerCase().includes("play() request was interrupted") ||
        msg.toLowerCase().includes("media was removed from the document")
      ) {
        event.preventDefault();
        event.stopImmediatePropagation?.();
      }
    };

    const handleError = (event) => {
      const msg = event?.message || "";
      if (
        msg.toLowerCase().includes("play() request was interrupted") ||
        msg.toLowerCase().includes("media was removed from the document")
      ) {
        event.preventDefault();
        event.stopImmediatePropagation?.();
      }
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection, true);
    window.addEventListener("error", handleError, true);
    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection, true);
      window.removeEventListener("error", handleError, true);
    };
  }, []);

  // Play pleasant scanner beep sound using Web Audio API
  const playBeep = useCallback(() => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.15);
    } catch {
      // AudioContext not permitted or supported, ignore
    }
  }, []);

  // Safe scanner stop
  const safeStopScanner = useCallback(async () => {
    if (isStoppingRef.current) return;
    isStoppingRef.current = true;

    // Clear native detector loop if running
    if (nativeDetectorTimerRef.current) {
      clearInterval(nativeDetectorTimerRef.current);
      nativeDetectorTimerRef.current = null;
    }

    try {
      const scanner = scannerRef.current;
      if (scanner) {
        if (scanner.isScanning) {
          try {
            await scanner.stop();
          } catch (e) {
            console.warn("Scanner stop warning:", e);
          }
        }
        try {
          await scanner.clear();
        } catch {
          // ignore clear error
        }
      }
    } finally {
      scannerRef.current = null;
      isStartingRef.current = false;
      isStoppingRef.current = false;
      setIsScanning(false);
    }
  }, []);

  // Handle successful barcode recognition
  const handleBarcodeDecoded = useCallback(
    async (decodedText) => {
      if (hasFoundRef.current) return;
      hasFoundRef.current = true;

      playBeep();
      await safeStopScanner();

      // Normalize barcode text (strip non-digits, take 13 digits if 18-digit EAN+5 was scanned)
      let clean = decodedText.split(" ")[0].replace(/[^0-9X]/gi, "").trim();
      if (clean.length === 18) {
        clean = clean.slice(0, 13);
      } else if (clean.length === 12) {
        clean = "0" + clean;
      }

      // Guard: Only proceed if it is a complete 10 or 13-digit ISBN
      if (clean.length !== 10 && clean.length !== 13) {
        hasFoundRef.current = false;
        return;
      }

      playBeep();
      await safeStopScanner();
      onBookFound(clean);
    },
    [onBookFound, playBeep, safeStopScanner]
  );

  // Setup parallel native BarcodeDetector loop on <video> element (in Chrome / Edge)
  const startNativeBarcodeDetectorLoop = useCallback(() => {
    if (typeof window === "undefined" || !("BarcodeDetector" in window)) {
      return;
    }

    try {
      const supportedFormats = [
        "ean_13",
        "ean_8",
        "code_128",
        "code_39",
        "upc_a",
        "upc_e",
        "qr_code",
      ];
      const detector = new window.BarcodeDetector({ formats: supportedFormats });

      if (nativeDetectorTimerRef.current) {
        clearInterval(nativeDetectorTimerRef.current);
      }

      nativeDetectorTimerRef.current = setInterval(async () => {
        if (hasFoundRef.current) return;

        const container = document.getElementById(containerId);
        const video = container?.querySelector("video");
        if (video && video.readyState >= 2 && !video.paused) {
          try {
            const detected = await detector.detect(video);
            if (detected && detected.length > 0) {
              const code = detected[0].rawValue;
              if (code) {
                console.log("⚡ Native BarcodeDetector found code:", code);
                handleBarcodeDecoded(code);
              }
            }
          } catch {
            // Frame detection error, continue next frame
          }
        }
      }, 150);
    } catch (e) {
      console.warn("Native BarcodeDetector init note:", e);
    }
  }, [handleBarcodeDecoded]);

  // Safe scanner start
  const safeStartScanner = useCallback(
    async (cameraId) => {
      if (isStartingRef.current) return;
      isStartingRef.current = true;
      hasFoundRef.current = false;
      setCameraError(null);
      setFrameCount(0);

      // Stop previous running scanner safely
      await safeStopScanner();

      const container = document.getElementById(containerId);
      if (!container) {
        isStartingRef.current = false;
        return;
      }

      try {
        const formatsToSupport = [
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.CODE_93,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.UPC_EAN_EXTENSION,
          Html5QrcodeSupportedFormats.QR_CODE,
        ];

        const qrScanner = new Html5Qrcode(containerId, {
          formatsToSupport,
          useBarCodeDetectorIfSupported: true,
          verbose: false,
        });
        scannerRef.current = qrScanner;

        const cameraConfig = cameraId
          ? { deviceId: { exact: cameraId } }
          : { facingMode: "environment" };

        const scanConfig = {
          fps: 20, // Faster frame rate for responsive scanning
          qrbox: (viewfinderWidth, viewfinderHeight) => {
            // Generous scan box so barcode is captured anywhere in the viewport
            return {
              width: Math.min(viewfinderWidth, Math.max(280, Math.floor(viewfinderWidth * 0.92))),
              height: Math.min(viewfinderHeight, Math.max(160, Math.floor(viewfinderHeight * 0.72))),
            };
          },
          disableFlip: false, // Reads mirrored camera streams (common on laptop webcams)
          videoConstraints: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        };

        await qrScanner.start(
          cameraConfig,
          scanConfig,
          (decodedText) => {
            handleBarcodeDecoded(decodedText);
          },
          () => {
            // Frame analyzed, increment frame counter for user feedback
            setFrameCount((prev) => (prev + 1) % 100);
          }
        );

        setIsScanning(true);

        // Start complementary high-speed native BarcodeDetector loop
        startNativeBarcodeDetectorLoop();
      } catch (err) {
        const msg = err?.message || String(err);
        if (
          !msg.toLowerCase().includes("play() request was interrupted") &&
          !msg.toLowerCase().includes("media was removed")
        ) {
          console.error("Camera start error:", err);
          const errorMsg =
            err?.message ||
            "Could not open camera. Please check camera permissions or use the manual ISBN option.";
          setCameraError(errorMsg);
        }
        setIsScanning(false);
      } finally {
        isStartingRef.current = false;
      }
    },
    [handleBarcodeDecoded, safeStopScanner, startNativeBarcodeDetectorLoop]
  );

  // Manage scanner lifecycle on modal open/close
  useEffect(() => {
    if (!isOpen || activeTab !== "camera") {
      safeStopScanner();
      return;
    }

    let isMounted = true;
    hasFoundRef.current = false;

    // Small delay to ensure modal DOM container is fully mounted and sized
    const timer = setTimeout(() => {
      if (!isMounted) return;

      // Start scanner immediately with environment/default camera
      safeStartScanner(selectedCameraId || null);

      // Query available cameras in background to populate switcher dropdown without restarting
      Html5Qrcode.getCameras()
        .then((devices) => {
          if (!isMounted) return;
          if (devices && devices.length > 0) {
            setCameras(devices);
          }
        })
        .catch(() => {});
    }, 150);

    return () => {
      isMounted = false;
      clearTimeout(timer);
      safeStopScanner();
    };
  }, [isOpen, activeTab]); // Only depends on isOpen and activeTab

  // Handle camera switch dropdown
  const handleCameraChange = (newCameraId) => {
    setSelectedCameraId(newCameraId);
    safeStartScanner(newCameraId);
  };

  // Handle manual ISBN submit
  const handleManualSubmit = async (e) => {
    if (e) e.preventDefault();
    const clean = manualIsbn.replace(/[^0-9X]/gi, "").trim();
    if (!clean || (clean.length !== 10 && clean.length !== 13)) {
      toast.error("Please enter a valid 10 or 13-digit ISBN.");
      return;
    }
    await safeStopScanner();
    onBookFound(clean);
  };

  // Quick fill sample ISBN for instant testing
  const handleQuickSampleIsbn = (isbn) => {
    setManualIsbn(isbn);
    onBookFound(isbn);
  };

  // Save Google Books API Key
  const handleSaveApiKey = (e) => {
    e.preventDefault();
    BookMetadataService.setApiKey(apiKeyInput);
    setIsKeySaved(true);
    toast.success("Google Books API Key saved successfully!");
    setTimeout(() => setIsKeySaved(false), 2500);
  };

  // Handle Image File Upload for Barcode Scanning
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsProcessingFile(true);
    try {
      const html5QrCode = new Html5Qrcode(containerId, {
        useBarCodeDetectorIfSupported: true,
        verbose: false,
      });

      const decodedText = await html5QrCode.scanFile(file, true);
      if (decodedText) {
        handleBarcodeDecoded(decodedText);
      } else {
        toast.error("No barcode detected in the uploaded image. Please try another image or enter ISBN.");
      }
    } catch (err) {
      console.warn("Scan file result:", err);
      toast.error("Could not read barcode from this image. Please ensure the barcode is clear.");
    } finally {
      setIsProcessingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleClose = async () => {
    await safeStopScanner();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/80">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-orange-600 text-white flex items-center justify-center font-bold text-sm">
              <Camera size={18} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white text-base">
                Scan Book Barcode
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Hold barcode in camera view or enter ISBN manually
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-100/60 dark:bg-gray-900/40 p-1 m-4 rounded-xl gap-1">
          <button
            type="button"
            onClick={() => setActiveTab("camera")}
            className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
              activeTab === "camera"
                ? "bg-white dark:bg-gray-800 text-orange-600 dark:text-orange-400 shadow-sm"
                : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            }`}
          >
            <Camera size={14} />
            Camera
          </button>

          <button
            type="button"
            onClick={async () => {
              await safeStopScanner();
              setActiveTab("upload");
            }}
            className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
              activeTab === "upload"
                ? "bg-white dark:bg-gray-800 text-orange-600 dark:text-orange-400 shadow-sm"
                : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            }`}
          >
            <Upload size={14} />
            Upload Photo
          </button>

          <button
            type="button"
            onClick={async () => {
              await safeStopScanner();
              setActiveTab("manual");
            }}
            className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
              activeTab === "manual"
                ? "bg-white dark:bg-gray-800 text-orange-600 dark:text-orange-400 shadow-sm"
                : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            }`}
          >
            <Search size={14} />
            Enter ISBN
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-6 flex-1 flex flex-col justify-center">
          {activeTab === "camera" && (
            <div className="flex flex-col items-center">
              {/* Scanner Viewport */}
              <div className="relative w-full aspect-[4/3] max-w-sm rounded-xl overflow-hidden bg-black border-2 border-orange-500/40 shadow-inner flex items-center justify-center">
                <ScannerVideoContainer containerId={containerId} />

                {/* Animated Scanner Laser Overlay */}
                {isScanning && !cameraError && (
                  <div className="absolute inset-0 pointer-events-none flex flex-col justify-center items-center">
                    <div className="w-[88%] h-[68%] border-2 border-dashed border-orange-400/90 rounded-xl relative shadow-[0_0_15px_rgba(249,115,22,0.2)]">
                      <div className="absolute inset-x-0 h-0.5 bg-red-500 shadow-[0_0_10px_#ef4444] animate-pulse top-1/2" />
                    </div>
                    <span className="text-[11px] font-medium text-white/95 bg-black/75 px-3 py-1 rounded-full mt-2.5 backdrop-blur-sm border border-white/10">
                      Align barcode anywhere inside box
                    </span>
                  </div>
                )}

                {/* Live Scanning Status Badge */}
                {isScanning && !cameraError && (
                  <div className="absolute top-2.5 left-2.5 z-10 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm text-green-400 text-[11px] font-medium px-2.5 py-1 rounded-full border border-green-500/30">
                    <span className="w-2 h-2 rounded-full bg-green-400 animate-ping" />
                    <span>Scanning live...</span>
                  </div>
                )}

                {/* Loading / Fetching overlay */}
                {isFetching && (
                  <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-3 z-20">
                    <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
                    <p className="text-white text-sm font-medium">Fetching book metadata...</p>
                  </div>
                )}
              </div>

              {/* Real-time Guidance Tips */}
              <div className="mt-3 w-full max-w-sm text-center">
                <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                  💡 <strong>Tip for Laptop Webcams:</strong> Hold book <strong>15–25 cm away</strong> until the black stripes look sharp and clear.
                </p>
              </div>

              {/* Camera Switcher (if multiple cameras available) */}
              {cameras.length > 1 && (
                <div className="mt-2.5 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>Camera:</span>
                  <select
                    value={selectedCameraId}
                    onChange={(e) => handleCameraChange(e.target.value)}
                    className="text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-gray-900 dark:text-white"
                  >
                    <option value="">Default (Rear / Primary)</option>
                    {cameras.map((cam) => (
                      <option key={cam.id} value={cam.id}>
                        {cam.label || `Camera ${cam.id.slice(0, 5)}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Camera Error Message */}
              {cameraError && (
                <div className="mt-4 p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-2.5 text-xs text-red-700 dark:text-red-300 w-full">
                  <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold">Camera Access Issue</p>
                    <p className="mt-0.5">{cameraError}</p>
                    <button
                      type="button"
                      onClick={() => setActiveTab("manual")}
                      className="mt-2 text-xs font-semibold text-orange-600 dark:text-orange-400 hover:underline"
                    >
                      Use manual ISBN entry instead →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "upload" && (
            <div className="flex flex-col items-center py-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl p-8 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-orange-500 dark:hover:border-orange-400 hover:bg-orange-50/50 dark:hover:bg-orange-950/20 transition-all text-center"
              >
                <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400 flex items-center justify-center">
                  <Upload size={24} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    Click to upload a barcode image
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Take a clear photo with your phone or select an image file (PNG, JPG)
                  </p>
                </div>
                <button
                  type="button"
                  disabled={isProcessingFile}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold rounded-lg shadow-sm"
                >
                  {isProcessingFile ? "Analyzing Barcode..." : "Choose Image"}
                </button>
              </div>
            </div>
          )}

          {activeTab === "manual" && (
            /* Manual ISBN Tab */
            <form onSubmit={handleManualSubmit} className="flex flex-col gap-4 py-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  ISBN Number (10 or 13 digits)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={manualIsbn}
                    onChange={(e) => setManualIsbn(e.target.value)}
                    placeholder="e.g. 9780143424123 or 9780062315007"
                    autoFocus
                    className="w-full px-4 py-2.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono tracking-wider"
                  />
                  <Search className="w-4 h-4 text-gray-400 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5">
                  Printed above or below the barcode on the back cover of any published book.
                </p>
              </div>

              {/* Quick Sample ISBNs for 1-click test */}
              <div className="bg-gray-50 dark:bg-gray-900/40 p-3 rounded-xl border border-gray-200 dark:border-gray-700">
                <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 flex items-center gap-1 mb-2">
                  <Sparkles size={12} className="text-orange-500" />
                  Quick Test Samples (Click to test):
                </span>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => handleQuickSampleIsbn("9780062315007")}
                    className="text-[11px] px-2.5 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:border-orange-500 hover:text-orange-600 rounded-lg text-gray-700 dark:text-gray-300 transition"
                  >
                    The Alchemist (9780062315007)
                  </button>
                  <button
                    type="button"
                    onClick={() => handleQuickSampleIsbn("9788173711466")}
                    className="text-[11px] px-2.5 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:border-orange-500 hover:text-orange-600 rounded-lg text-gray-700 dark:text-gray-300 transition"
                  >
                    Wings of Fire (9788173711466)
                  </button>
                  <button
                    type="button"
                    onClick={() => handleQuickSampleIsbn("9781612681139")}
                    className="text-[11px] px-2.5 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:border-orange-500 hover:text-orange-600 rounded-lg text-gray-700 dark:text-gray-300 transition"
                  >
                    Rich Dad Poor Dad (9781612681139)
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isFetching || !manualIsbn.trim()}
                className="w-full py-2.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-medium text-sm rounded-xl shadow-md transition-all flex items-center justify-center gap-2 mt-1"
              >
                {isFetching ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Fetching Book Info...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    Auto-Fill Book Details
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* ─── Google Books API Key Configuration (Optional) ──────────────── */}
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/60 px-6 py-3">
          <button
            type="button"
            onClick={() => setShowApiKeySetting((prev) => !prev)}
            className="w-full flex items-center justify-between text-xs font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition"
          >
            <span className="flex items-center gap-1.5">
              <Key size={13} className="text-orange-500" />
              <span>Google Books API Key</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-normal ${
                apiKeyInput ? "bg-green-100 text-green-700 dark:bg-green-950/60 dark:text-green-400" : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
              }`}>
                {apiKeyInput ? "Active" : "Optional"}
              </span>
            </span>
            {showApiKeySetting ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {showApiKeySetting && (
            <form onSubmit={handleSaveApiKey} className="mt-3 flex flex-col gap-2">
              <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                Google Books requires a free API key to avoid 429 rate-limiting on regional Indian books. Get yours in 1 minute on{" "}
                <a
                  href="https://console.cloud.google.com/apis/credentials"
                  target="_blank"
                  rel="noreferrer"
                  className="text-orange-600 dark:text-orange-400 underline font-medium"
                >
                  Google Cloud Credentials
                </a>{" "}
                (1,000 lookups/day free).
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder="Paste AIzaSy... API key here"
                  className="flex-1 px-3 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white font-mono placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-500"
                />
                <button
                  type="submit"
                  className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold rounded-lg shadow-sm flex items-center gap-1"
                >
                  {isKeySaved ? <Check size={14} /> : "Save"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
